import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import "@/App.css";
import { PageHeader } from "@/sections";
import { Upload, Send, Quote, MapPin, Link as LinkIcon } from "lucide-react";

const MAX_BYTES = 8 * 1024 * 1024;

function StoryCard({ s }) {
  return (
    <article className="admin-card overflow-hidden flex flex-col" data-testid={`story-${s.id}`}>
      {s.photo && (
        <img src={s.photo} alt={s.name} className="w-full aspect-[4/3] object-cover" />
      )}
      <div className="p-6 md:p-8 flex-1 flex flex-col">
        <Quote size={26} className="text-[var(--si-terracotta)] mb-3" />
        <p className="text-[var(--si-text-2)] leading-relaxed text-base md:text-lg italic whitespace-pre-wrap mb-6">
          {s.story}
        </p>
        <div className="mt-auto pt-4 border-t border-[var(--si-border)]">
          <p className="font-display text-xl">{s.name}</p>
          {(s.location || s.instrument) && (
            <p className="label-eyebrow mt-1 flex items-center gap-2 flex-wrap">
              {s.location && (<><MapPin size={11} />{s.location}</>)}
              {s.location && s.instrument && " · "}
              {s.instrument && s.instrument}
            </p>
          )}
          {s.personal_link && (
            <a
              href={s.personal_link.startsWith("http") ? s.personal_link : `https://${s.personal_link}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-sm text-[var(--si-terracotta)] hover:underline"
              data-testid={`story-link-${s.id}`}
            >
              <LinkIcon size={13} /> Visit their link →
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

function ShareForm({ onSubmitted }) {
  const [form, setForm] = useState({ name: "", location: "", instrument: "", story: "", photo: "", personal_link: "" });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const fileRef = useRef(null);

  const onFile = (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setStatus({ ok: false, msg: `Photo too large (max ${MAX_BYTES / 1024 / 1024} MB).` });
      return;
    }
    const r = new FileReader();
    r.onload = () => setForm((f) => ({ ...f, photo: r.result }));
    r.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      await api.post("/stories", form);
      setStatus({
        ok: true,
        msg: "Thank you for sharing your story! It will appear here once reviewed by Auburn Druid.",
      });
      setForm({ name: "", location: "", instrument: "", story: "", photo: "", personal_link: "" });
      onSubmitted?.();
    } catch (err) {
      setStatus({ ok: false, msg: formatApiError(err) });
    } finally { setSubmitting(false); }
  };

  return (
    <section className="px-6 md:px-12 lg:px-24 pb-24">
      <div className="max-w-3xl mx-auto admin-card p-8 md:p-10" data-testid="share-story-form">
        <p className="label-eyebrow mb-3">Share your story</p>
        <h2 className="font-display text-3xl md:text-4xl font-light mb-3">Tell us your experience.</h2>
        <p className="text-[var(--si-text-2)] mb-8">
          If you own a Pharaoh Love Flute, drum or didgeridoo from this studio, we'd be honoured to hear your story. Your words may inspire someone else to find their voice through music.
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <input required className="input-field" placeholder="Your name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="story-name" />
            <input className="input-field" placeholder="Where you're from (optional)" value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="story-location" />
          </div>
          <input className="input-field" placeholder='Which instrument? (e.g. "Black Maire Love Flute") — optional' value={form.instrument}
            onChange={(e) => setForm({ ...form, instrument: e.target.value })} data-testid="story-instrument" />
          <textarea required rows={6} className="input-field" placeholder="Share your story — how the instrument came to you, how it changed something, the first time you played it..." value={form.story}
            onChange={(e) => setForm({ ...form, story: e.target.value })} data-testid="story-text" />

          <div>
            <label className="label-eyebrow block mb-2">Your link (Instagram, website, anywhere) — optional</label>
            <input className="input-field" type="url" placeholder="https://www.instagram.com/yourhandle or https://yourwebsite.com" value={form.personal_link}
              onChange={(e) => setForm({ ...form, personal_link: e.target.value })} data-testid="story-link" />
            <p className="text-xs text-[var(--si-text-2)] mt-2">
              Visitors will see a small link button on your story so they can follow you back.
            </p>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">A photo with your instrument (optional)</label>
            <div className="flex items-start gap-4">
              {form.photo ? (
                <img src={form.photo} alt="preview" className="w-24 h-24 rounded-md object-cover border border-[var(--si-border)]" />
              ) : (
                <div className="w-24 h-24 rounded-md bg-[var(--si-bg-2)] border border-dashed border-[var(--si-border)] flex items-center justify-center text-xs text-[var(--si-text-2)] text-center px-2">
                  Add a photo
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input type="file" accept="image/*" hidden ref={fileRef} onChange={(e) => onFile(e.target.files?.[0])} data-testid="story-photo-file" />
                <button type="button" onClick={() => fileRef.current?.click()} className="btn-outline text-sm py-2 px-4" data-testid="story-photo-upload">
                  <Upload size={14} /> Upload photo
                </button>
                {form.photo && (
                  <button type="button" className="text-xs text-red-700 hover:underline block" onClick={() => setForm({ ...form, photo: "" })}>
                    Clear photo
                  </button>
                )}
              </div>
            </div>
          </div>

          <button type="submit" disabled={submitting} className="btn-primary" data-testid="story-submit">
            {submitting ? "Sending..." : "Share my story"} <Send size={16} />
          </button>
          {status && (
            <p className={`text-sm ${status.ok ? "text-[var(--si-forest)]" : "text-red-700"}`} data-testid="story-status">
              {status.msg}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}

export default function StoriesPage() {
  const [content, setContent] = useState(null);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/stories").then((r) => setStories(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => {});
    load();
  }, []);

  return (
    <div className="App">
      <Nav artisanName={content?.artisan_name || "Sacred Instruments"} />
      <PageHeader
        eyebrow="People · Voices · Experiences"
        title="Stories from those who play"
        subtitle="Real words from people whose lives have been touched by a Pharaoh Love Flute, drum or didgeridoo from the Waitetuna workshop. Share yours below."
      />

      <section className="px-6 md:px-12 lg:px-24 pb-16">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <p className="text-[var(--si-text-2)]">Loading...</p>
          ) : stories.length === 0 ? (
            <div className="admin-card p-12 text-center">
              <Quote className="mx-auto mb-4 text-[var(--si-terracotta)]" size={36} />
              <p className="text-[var(--si-text-2)] max-w-xl mx-auto">
                No stories have been shared yet. If you own a Pharaoh Love Flute, drum or didgeridoo from this studio, we'd be honoured to hear your experience. Your words may inspire someone else to find their voice through music.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {stories.map((s) => <StoryCard key={s.id} s={s} />)}
            </div>
          )}
        </div>
      </section>

      <ShareForm onSubmitted={load} />
      <Footer content={content || {}} />
    </div>
  );
}
